import {
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { createIdFromText } from 'helpers/FormatText';
import React, { useMemo, useRef, useState } from 'react';
import TextRender from '../TextRender/TextRender';
import { useStyles } from './Dropdown.styles';

interface IDropdown {
  id?: string;
  dataAutomation?: string;
  disabled?: boolean;
  changeFunction: (selected: any) => void;
  selectedValue?: string | undefined;
  defaultValue?: string;
  placeholder?: string;
  options?: IDropdownOption[];
  classNames?: {
    container?: string;
    placeholder?: string;
    options?: string;
  };
  hideInputOnExpand?: boolean;
}

const overInputModifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, -42],
    },
  },
  {
    name: 'flip',
    enabled: false,
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true,
      rootBoundary: 'viewport',
      padding: 10,
    },
  },
];

export interface IDropdownOption {
  label: string;
  value: any;
}
// Convert string[] to IDropdownOption[]
export const createDropdownItemObj = (value: string) => ({
  label: value,
  value: createIdFromText(value),
});

const Dropdown = ({
  dataAutomation,
  changeFunction,
  selectedValue,
  defaultValue,
  options,
  disabled,
  placeholder,
  classNames,
  hideInputOnExpand,
}: IDropdown): JSX.Element => {
  const classes = useStyles();

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const modifiers = hideInputOnExpand ? overInputModifiers : undefined;

  const selectedValueProcessed = useMemo(
    () =>
      options?.find(({ value }) => {
        const lowerValue = value?.toLowerCase() || '';
        const lowerSelected = selectedValue?.toLowerCase() || '';
        const lowerDefault = defaultValue?.toLowerCase() || '';
        return lowerValue === lowerSelected || lowerValue === lowerDefault;
      }),
    [options, selectedValue, defaultValue]
  );

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
      document.body.classList.remove('acr-focus-disabled');
    }
  };

  const handleListItemKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab' || event.key === 'Enter') {
      event.preventDefault();
      setIsOpen(false);
      buttonRef.current?.focus();
      document.body.classList.remove('acr-focus-disabled');
    }
  };

  const handleOptionSelection = (value: string) => {
    changeFunction(value);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`${classes.container} ${classNames?.container || ''}`}
    >
      <Button
        ref={buttonRef}
        aria-controls={isOpen ? 'composition-menu' : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        aria-haspopup="true"
        onClick={toggleDropdown}
        data-automation={`${dataAutomation}-toggle`}
        disableRipple={true}
        className={`drop-main-item ${classes.placeholder} ${
          classNames?.placeholder || ''
        } ${isOpen ? 'select-open' : ''}`}
        disabled={disabled}
      >
        <TextRender text={selectedValueProcessed?.label || placeholder || ''} />
      </Button>
      {options && (
        <Popper
          open={isOpen}
          anchorEl={containerRef.current}
          placement="bottom-start"
          modifiers={modifiers}
          transition
          disablePortal
          className={classes.popper}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-start' ? 'left top' : 'left bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={closeDropdown}>
                  <MenuList
                    autoFocusItem={isOpen}
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                    className={`${classes.menu_container} ${
                      hideInputOnExpand ? 'hide-input' : ''
                    }`}
                  >
                    {options.map((option: IDropdownOption) => (
                      <MenuItem
                        data-automation={option.value}
                        key={option.value}
                        onKeyDown={handleListItemKeyDown}
                        className={`drop-menu-item ${classes.menu_item} ${
                          classNames?.options || 'acr-paragraph-5'
                        }`}
                        onClick={() => handleOptionSelection(option.value)}
                      >
                        <TextRender text={option.label} />
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};

export default Dropdown;
